/**
 * WebAPI Connection Layer
 * Connects to Google Apps Script WebAPI to fetch sales data
 */

import { useState, useEffect, useRef } from 'react';
import {
  fetchCanonicalCalls,
  buildDailyCallsIndex,
  spliceDailyCanonicalIntoLegacy,
  thirteenMonthWindow
} from './utils/calls';

// API Configuration
const API_CONFIG = {
  // Salesforce data API
  WEB_APP_URL: 'https://script.google.com/macros/s/AKfycby4Xh43K4O4FFFbfIxA3510f1LnLQ1ne3rQ8AtJOEaahefJqjO9HSkG0FgSOVH1HK7T/exec',
  API_KEY: 'your-secret-key-12345',

  // Apollo email performance data API
  APOLLO_API_URL: 'https://script.google.com/macros/s/AKfycbwwoLIucfWmxu0phzQ6myoyx4Sdm04di8XLMgIWBxgA34aIliLe13F5iTrj-MgoZ_Ku/exec',

  // Company Intel Research API (separate GAS deployment)
  RESEARCH_API_URL: 'https://script.google.com/macros/s/AKfycbye7dqTLqTFHk0F_KJ2LQcN095r_FeQ_RwtLOOZhTqVzt6Q6k4aIj6M2tpFaV4-FOiT/exec'
};

export const WEB_APP_URL = API_CONFIG.WEB_APP_URL;

/**
 * Fetch data from WebAPI
 * @param {string} dataType - Type of data to fetch ('all', 'mtd', 'raw_tab_data', etc.)
 * @param {object} params - Additional query parameters
 */
export async function fetchData(dataType = 'all', params = {}, options = {}) {
  // options.timeoutMs (opt-in): abort a stalled GAS call instead of hanging
  // forever. Existing callers pass no options → no timeout → unchanged behavior.
  // Only opted-in calls (e.g. the legacy Book SMS pull) get a deadline.
  const { timeoutMs } = options;
  let controller = null;
  let timer = null;
  if (timeoutMs && typeof AbortController !== 'undefined') {
    controller = new AbortController();
    timer = setTimeout(function () { controller.abort(); }, timeoutMs);
  }
  try {
    const queryParams = new URLSearchParams({
      key: API_CONFIG.API_KEY,
      type: dataType,
      _t: Date.now(), // Cache-busting timestamp
      ...params
    });

    const url = `${API_CONFIG.WEB_APP_URL}?${queryParams}`;
    console.log(`📡 Fetching from WebAPI: ${dataType}`, params);

    const response = await fetch(url, controller ? { signal: controller.signal } : undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Data received:`, data);
    return data;
  } catch (error) {
    if (error && error.name === 'AbortError') {
      const msg = `WebAPI timeout after ${timeoutMs}ms (${dataType})`;
      console.error(`❌ ${msg}`);
      throw new Error(msg);
    }
    console.error('❌ WebAPI fetch error:', error);
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Fetch data via GET with base64-encoded payload.
 * GAS doPost strips the body on 302 redirects — research calls must use GET.
 */
export async function fetchDataGet(action, params = {}) {
  try {
    const url = new URL(API_CONFIG.RESEARCH_API_URL);
    url.searchParams.set('action', action);

    // If only account_id, pass as plain param (getCompanyIntelProfile reads it directly)
    // For runCompanyResearch, encode full payload as base64
    if (params.account_id && Object.keys(params).length === 1) {
      url.searchParams.set('account_id', params.account_id);
    } else if (Object.keys(params).length > 0) {
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(params))));
      url.searchParams.set('payload', encoded);
    }

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (err) {
    console.error('fetchDataGet error:', err);
    throw err;
  }
}

/**
 * React Hook - Fetch dashboard data with SINGLE-CALL LOADING
 * Loads all data in one call, auto-refreshes every 5 minutes
 * NOTE: Two-tier loading disabled until WebAPI period filtering is fixed
 */
export function useDashboardData({ skip = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [capacityWarning, setCapacityWarning] = useState(null);

  const isFirstLoad = useRef(true);
  const hasLoadedRef = useRef(false);
  const ROW_LIMIT = 100000;

  // Main load function - single call for all data
  const loadData = async () => {
    if (hasLoadedRef.current) return;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('📡 Loading all dashboard data...');
      const result = await fetchData('raw_tab_data', { limit: ROW_LIMIT, period: 'current' });

      // Capacity check
      if (result && result.tabs) {
        let totalFetched = 0;

        // Log summary table
        console.log('┌─────────────────────────────────────────────────┐');
        console.log('│           📊 DATA LOAD SUMMARY                  │');
        console.log('├─────────────────┬───────────────────────────────┤');
        console.log('│ Tab             │ Rows Fetched                  │');
        console.log('├─────────────────┼───────────────────────────────┤');

        Object.entries(result.tabs).forEach(([tabName, tab]) => {
          const rows = tab?.rowsFetched || tab?.data?.length || 0;
          totalFetched += rows;
          console.log(`│ ${tabName.padEnd(15)} │ ${rows.toLocaleString().padStart(29)} │`);
        });

        console.log('├─────────────────┼───────────────────────────────┤');
        console.log(`│ TOTAL           │ ${totalFetched.toLocaleString().padStart(29)} │`);
        console.log('└─────────────────┴───────────────────────────────┘');

        const capacityPercent = (totalFetched / ROW_LIMIT) * 100;

        if (totalFetched >= ROW_LIMIT) {
          setCapacityWarning({
            level: 'critical',
            message: `🚨 DATA TRUNCATED: ${totalFetched.toLocaleString()} rows hit limit. Some data MISSING!`,
            percent: 100
          });
          console.error(`🚨 CRITICAL: Data truncated at ${ROW_LIMIT.toLocaleString()} rows!`);
        } else if (capacityPercent >= 90) {
          setCapacityWarning({
            level: 'warning',
            message: `⚠️ Capacity at ${capacityPercent.toFixed(0)}%: ${totalFetched.toLocaleString()} rows`,
            percent: capacityPercent
          });
          console.warn(`⚠️ WARNING: ${capacityPercent.toFixed(1)}% capacity used`);
        } else {
          setCapacityWarning(null);
          console.log(`✅ Capacity OK: ${capacityPercent.toFixed(1)}% (${totalFetched.toLocaleString()} / ${ROW_LIMIT.toLocaleString()})`);
        }
      }

      setData(result);
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }

    } catch (err) {
      setError(err.message);
      console.error('❌ Data load failed:', err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      hasLoadedRef.current = true;
    }
  };

  // Refresh function
  const refresh = async () => {
    hasLoadedRef.current = false;
    await loadData();
  };

  useEffect(() => {
    if (skip) return;
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) {
        await loadData();
      }
    };

    wrappedLoadData();

    // Auto-refresh every 5 minutes
    const interval = setInterval(async () => {
      if (isMounted) {
        hasLoadedRef.current = false;
        await loadData();
      }
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, capacityWarning };
}

/**
 * React Hook - Fetch goals data
 * Makes a separate API call for Monthly_Goals tab only (limit: 100 rows).
 */
export function useGoalsData({ skip = false } = {}) {
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (skip) return;
    let isMounted = true;

    async function loadGoals() {
      try {
        // Only show loading spinner on first load, not on background refreshes
        if (isFirstLoad.current) {
          setLoading(true);
        }

        const result = await fetchData('raw_tab_data', {
          tab: 'Monthly_Goals',
          limit: 100
        });

        if (isMounted && result && result.tabs && result.tabs.Monthly_Goals) {
          setGoals(result.tabs.Monthly_Goals.data);
          setError(null);

          // On first load, set loading=false and mark as complete
          if (isFirstLoad.current) {
            setLoading(false);
            isFirstLoad.current = false;
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          // On error during first load, still set loading=false
          if (isFirstLoad.current) {
            setLoading(false);
          }
        }
      }
    }

    loadGoals();

    return () => {
      isMounted = false;
    };
  }, []);

  return { goals, loading, error };
}

/**
 * React Hook - Lightweight Salesforce fetch for standalone Book mode
 * Fetches ONLY Dials_TalkTime + SMS tabs (instead of all 8 tabs)
 * Used when ?tab=book&standalone=1 to dramatically reduce load time
 */
export function useBookSalesforceData({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);
  const isLoadingRef = useRef(false);
  const intervalRef = useRef(null);
  const ROW_LIMIT = 100000;

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('📚 Loading Book Salesforce data (Dials_TalkTime canonical + SMS legacy)...');

      // ws-sales-calls-sms-migration D-SCSM-1/3/5 — Dials_TalkTime comes from canonical
      // (Phase 1 route, last 13mo, team=Renewals). SMS stays on legacy GAS per D-SCSM-2.
      const window13 = thirteenMonthWindow();
      // allSettled (not all): the legacy GAS SMS pull can stall indefinitely;
      // a single hung fetch must NOT block calls from rendering (the Book "on/off
      // crash" was this hang). SMS gets a 45s deadline so allSettled can resolve.
      const SMS_TIMEOUT_MS = 45000;
      const settled = await Promise.allSettled([
        fetchCanonicalCalls(window13.start, window13.end, 'Renewals').then(rows => ({ tabs: { Dials_TalkTime: { data: rows } } })),
        fetchData('raw_tab_data', { tab: 'SMS', limit: ROW_LIMIT }, { timeoutMs: SMS_TIMEOUT_MS })
      ]);
      const dialsResult = settled[0].status === 'fulfilled' ? settled[0].value : null;
      const smsResult = settled[1].status === 'fulfilled' ? settled[1].value : null;
      if (settled[0].status === 'rejected') {
        console.error('❌ Book calls (canonical) failed:', settled[0].reason?.message || settled[0].reason);
      }
      if (settled[1].status === 'rejected') {
        console.warn('⚠️ Book SMS (legacy GAS) failed/timed out — rendering without SMS:', settled[1].reason?.message || settled[1].reason);
      }

      // Merge into same shape as useDashboardData returns (each tab degrades to {} if its fetch failed)
      const merged = {
        tabs: {
          ...(dialsResult?.tabs || {}),
          ...(smsResult?.tabs || {})
        }
      };

      const dialsRows = dialsResult?.tabs?.Dials_TalkTime?.data?.length || 0;
      const smsRows = smsResult?.tabs?.SMS?.data?.length || 0;
      console.log(`✅ Book SF data loaded (calls=canonical, sms=legacy): Dials_TalkTime=${dialsRows} rows, SMS=${smsRows} rows`);

      setData(merged);
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('❌ Book SF data load failed:', err.message);
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  const refresh = async () => {
    await loadData();
  };

  const load = async () => {
    await loadData();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => loadData(), 30 * 60 * 1000);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) await loadData();
    };

    if (autoLoad) {
      wrappedLoadData();
      intervalRef.current = setInterval(wrappedLoadData, 30 * 60 * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, load };
}

/**
 * =============================================================================
 * HISTORICAL ACTIVITY DATA HOOK (March 2026 — Two-Tier Loading)
 * =============================================================================
 * Tier 2 background fetch: Loads FULL SMS + Dials_TalkTime history (no period
 * filter) after the Tier 1 current-month data has already rendered. This fills
 * the data gap for Activity tab date selectors, Yearly Review period selectors,
 * Goal Tracking 6-month rolling averages, and Performance tab period filtering.
 *
 * Pattern follows useBookSalesforceData (same fetchData call shape).
 * =============================================================================
 */
export function useHistoricalActivityData({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isLoadingRef = useRef(false);
  const ROW_LIMIT = 100000;

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    try {
      console.log('📊 [Tier 2] Loading historical SMS + Dials_TalkTime (13-month window)...');

      const [dialsResult, smsResult] = await Promise.all([
        fetchData('raw_tab_data', { tab: 'Dials_TalkTime', limit: ROW_LIMIT }),
        fetchData('raw_tab_data', { tab: 'SMS', limit: ROW_LIMIT })
      ]);

      const dialsRows = dialsResult?.tabs?.Dials_TalkTime?.data || [];
      const smsRows = smsResult?.tabs?.SMS?.data || [];

      console.log(`✅ [Tier 2] Historical data loaded: Dials_TalkTime=${dialsRows.length} rows, SMS=${smsRows.length} rows`);

      setData({
        Dials_TalkTime: dialsRows,
        SMS: smsRows
      });
      setError(null);
    } catch (err) {
      console.error('❌ [Tier 2] Historical activity data load failed:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  const load = async () => {
    await loadData();
  };

  useEffect(() => {
    if (autoLoad) {
      loadData();
    }
  }, []);

  return { data, loading, error, load };
}

/**
 * =============================================================================
 * NEW APOLLO EMAIL DATA HOOK (December 2025)
 * =============================================================================
 * Fetches data from 3 Apollo tabs in Google Sheets:
 * - Apollo_Daily_Metrics: Daily metrics per rep (UPSERT 4x daily)
 * - Apollo_Weekly_History: Weekly YTD backfill (246 rows)
 * - Apollo_Rep_Performance: Rolling 30-day view
 *
 * CRITICAL: This is a FRESH implementation. DO NOT use the old useApolloData()
 * or fetchApolloData() functions below - they use a different API/data structure.
 * =============================================================================
 */

/**
 * React Hook - Fetch Apollo Email Performance Data (NEW - December 2025)
 * Fetches from 3 Apollo tabs in parallel, auto-refreshes every 5 minutes
 */
export function useApolloEmailData({ autoLoad = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);
  const isLoadingRef = useRef(false);
  const intervalRef = useRef(null);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('📧 Loading Apollo email data from 3 tabs...');

      // Fetch all 3 Apollo tabs in parallel for efficiency
      const [dailyResult, weeklyResult, repResult] = await Promise.all([
        fetchData('raw_tab_data', { tab: 'Apollo_Daily_Metrics', limit: 5000 }),
        fetchData('raw_tab_data', { tab: 'Apollo_Weekly_History', limit: 1000 }),
        fetchData('raw_tab_data', { tab: 'Apollo_Rep_Performance', limit: 100 })
      ]);

      // Extract data from each result
      const dailyMetrics = dailyResult?.tabs?.Apollo_Daily_Metrics?.data || [];
      const weeklyHistory = weeklyResult?.tabs?.Apollo_Weekly_History?.data || [];
      const repPerformance = repResult?.tabs?.Apollo_Rep_Performance?.data || [];

      console.log('📊 Apollo data loaded:');
      console.log(`   - Daily Metrics: ${dailyMetrics.length} rows`);
      console.log(`   - Weekly History: ${weeklyHistory.length} rows`);
      console.log(`   - Rep Performance: ${repPerformance.length} rows`);

      setData({
        dailyMetrics,
        weeklyHistory,
        repPerformance
      });
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('❌ Apollo email data load failed:', err.message);
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  const refresh = async () => {
    await loadData();
  };

  const load = async () => {
    await loadData();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => loadData(), 5 * 60 * 1000);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) {
        await loadData();
      }
    };

    if (autoLoad) {
      wrappedLoadData();
      intervalRef.current = setInterval(wrappedLoadData, 5 * 60 * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, load };
}

/**
 * React Hook - Fetch Book data from iPaaS Master tab
 * Contains account assignments, eligibility status, cooldown, and owner info
 * Used by the Book tab to show account inventory and coverage metrics
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh }
 */
export function useBookData({ autoLoad = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);
  const isLoadingRef = useRef(false);
  const intervalRef = useRef(null);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('📚 Loading Book data from iPaaS...');
      const result = await fetchData('book_data');

      if (result.error) {
        throw new Error(result.error);
      }

      console.log(`✅ Book data loaded: ${result.data?.length || 0} accounts`);

      setData(result.data);
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('❌ Book data load failed:', err.message);
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  const refresh = async () => {
    await loadData();
  };

  const load = async () => {
    await loadData();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => loadData(), 5 * 60 * 1000);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) await loadData();
    };

    if (autoLoad) {
      // Initial load
      wrappedLoadData();

      // Auto-refresh every 5 minutes (same as other data hooks)
      intervalRef.current = setInterval(wrappedLoadData, 5 * 60 * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, load };
}

/**
 * React Hook - Fetch Apollo Contact Emails from iPaaS
 * Returns individual email records for Book tab coverage tracking
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh }
 */
export function useApolloContactEmails({ autoLoad = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);
  const isLoadingRef = useRef(false);
  const intervalRef = useRef(null);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('📧 Loading Apollo Contact Emails from iPaaS...');
      const result = await fetchData('apollo_contact_emails');

      if (result.error) {
        throw new Error(result.error);
      }

      console.log(`✅ Apollo emails loaded: ${result.data?.length || 0} records`);

      setData(result.data);
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('❌ Apollo emails load failed:', err.message);
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  const refresh = async () => {
    await loadData();
  };

  const load = async () => {
    await loadData();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => loadData(), 5 * 60 * 1000);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) await loadData();
    };

    if (autoLoad) {
      // Initial load
      wrappedLoadData();

      // Auto-refresh every 5 minutes
      intervalRef.current = setInterval(wrappedLoadData, 5 * 60 * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, load };
}

/**
 * React Hook - Fetch Pipeline Approvals data for Funnel Tab
 * Returns approval amounts from Pipeline spreadsheet for accurate $ calculations
 * Used to join with Opportunity_ID and get amounts at Approved, Contract Out, Contract In stages
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh }
 */
export function usePipelineApprovals({ autoLoad = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);
  const isLoadingRef = useRef(false);
  const intervalRef = useRef(null);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('💰 Loading Pipeline Approvals data...');
      const result = await fetchData('pipeline_approvals');

      if (result.error) {
        throw new Error(result.error);
      }

      console.log(`✅ Pipeline data loaded:`);
      console.log(`   - Approvals (active): ${result.approvals?.totalRows || 0} rows`);
      console.log(`   - Historical Approvals (funded): ${result.historicalApprovals?.totalRows || 0} rows`);
      console.log(`   - Historical Submissions (lender data): ${result.historicalSubmissions?.totalRows || 0} rows`);
      console.log(`   - Submissions (active pipeline): ${result.submissions?.totalRows || 0} rows`);

      setData({
        approvals: result.approvals?.data || [],
        historicalApprovals: result.historicalApprovals?.data || [],
        historicalSubmissions: result.historicalSubmissions?.data || [],
        submissions: result.submissions?.data || []
      });
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('❌ Pipeline data load failed:', err.message);
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  const refresh = async () => {
    await loadData();
  };

  const load = async () => {
    await loadData();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => loadData(), 5 * 60 * 1000);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) await loadData();
    };

    if (autoLoad) {
      // Initial load
      wrappedLoadData();

      // Auto-refresh every 5 minutes
      intervalRef.current = setInterval(wrappedLoadData, 5 * 60 * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, load };
}

/**
 * React Hook - Fetch Historical Funded data from Pipeline spreadsheet
 * Returns all funded deals for portfolio detail and Renewal Status
 * Used by Book Tab for account card, deal history, and pipeline metrics
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh, load }
 */
export function useHistoricalFunded({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);
  const isLoadingRef = useRef(false);
  const intervalRef = useRef(null);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('📊 Loading Historical Funded data from Pipeline...');
      const result = await fetchData('historical_funded');

      if (result.error) {
        throw new Error(result.error);
      }

      console.log(`✅ Historical Funded loaded: ${result.data?.length || 0} deals`);

      setData(result.data);
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('❌ Historical Funded load failed:', err.message);
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  const refresh = async () => {
    await loadData();
  };

  const load = async () => {
    await loadData();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => loadData(), 5 * 60 * 1000);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) await loadData();
    };

    if (autoLoad) {
      wrappedLoadData();
      intervalRef.current = setInterval(wrappedLoadData, 5 * 60 * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, load };
}

/**
 * React Hook - Fetch Datamerch Status data from iPaaS spreadsheet
 * Returns accounts flagged in Datamerch (industry-wide defaults)
 * Used by Book Tab for workability logic, account card warnings, and alerts
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh, load }
 */
export function useDatamerchStatus({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);
  const isLoadingRef = useRef(false);
  const intervalRef = useRef(null);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      console.log('⛔ Loading Datamerch Status data from iPaaS...');
      const result = await fetchData('datamerch_status');

      if (result.error) {
        throw new Error(result.error);
      }

      console.log(`✅ Datamerch Status loaded: ${result.data?.length || 0} flagged accounts`);

      setData(result.data);
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('❌ Datamerch Status load failed:', err.message);
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  const refresh = async () => {
    await loadData();
  };

  const load = async () => {
    await loadData();
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => loadData(), 5 * 60 * 1000);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) await loadData();
    };

    if (autoLoad) {
      wrappedLoadData();
      intervalRef.current = setInterval(wrappedLoadData, 5 * 60 * 1000);
    }

    return () => {
      isMounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh, load };
}

/**
 * React Hook - Fetch Payment Alert data from Payment Tracker GAS deployment
 * Returns accounts with payment issues (missed payments, NSF, etc.)
 * Used by Book Tab for workability override and alert display
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh, load }
 */
export function usePaymentAlerts({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isLoadingRef = useRef(false);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const url = 'https://script.google.com/macros/s/AKfycbzQbHHp7Te10s6A3TUrImmPclrE7qVq-OJE9pvLwQnSzQfEbL5UEAI52FGqYJM4cnWV/exec?type=payment_alerts';
      console.log('💳 Loading Payment Alert data from Payment Tracker...');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      const alerts = Array.isArray(result) ? result : [];
      console.log(`✅ Payment alerts loaded: ${alerts.length} records`);
      setData(alerts);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('❌ Payment alerts load failed:', err.message);
      setError(err.message);
      setData([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (autoLoad) loadData();
  }, []);

  return { data, loading, error, lastUpdated, refresh: loadData, load: loadData };
}

/**
 * React Hook - Fetch Account Status Overrides from iPaaS sheet
 * Phase 4 source-of-truth for account-level status + notes (replaces cached field
 * from Master_Salesforce_Data — that field is deprecated for current values).
 * Returns map: { [accountId]: { accountName, status, signalSource, lastUpdated, notes } }
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh, load }
 */
export function useAccountStatusOverrides({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isLoadingRef = useRef(false);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      console.log('🏷️  Loading Account Status Overrides...');
      const result = await fetchData('accountStatusOverrides');
      const overrides = (result && typeof result === 'object' && !Array.isArray(result) && !result.error) ? result : {};
      console.log(`✅ Account Status Overrides loaded: ${Object.keys(overrides).length} accounts`);
      setData(overrides);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('❌ Account Status Overrides load failed:', err.message);
      setError(err.message);
      setData({});
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (autoLoad) loadData();
  }, []);

  return { data, loading, error, lastUpdated, refresh: loadData, load: loadData };
}

/**
 * React Hook - Fetch Deal Level Status Overrides from iPaaS sheet
 * Phase 4 — keyed by Opportunity ID. Returns map of explicit deal-level overrides;
 * deals NOT in this map inherit account-level via the resolution function.
 * Returns map: { [opportunityId]: { accountId, accountName, status, signalSource, lastUpdated } }
 *
 * @returns {Object} { data, loading, error, lastUpdated, refresh, load }
 */
export function useDealLevelStatusOverrides({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isLoadingRef = useRef(false);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      console.log('📋 Loading Deal Level Status Overrides...');
      const result = await fetchData('dealLevelStatusOverrides');
      const overrides = (result && typeof result === 'object' && !Array.isArray(result) && !result.error) ? result : {};
      console.log(`✅ Deal Level Status Overrides loaded: ${Object.keys(overrides).length} deals`);
      setData(overrides);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('❌ Deal Level Status Overrides load failed:', err.message);
      setError(err.message);
      setData({});
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (autoLoad) loadData();
  }, []);

  return { data, loading, error, lastUpdated, refresh: loadData, load: loadData };
}

/**
 * =============================================================================
 * OLD APOLLO CODE BELOW - DO NOT USE
 * =============================================================================
 * The code below is from a SCRAPPED Apollo integration attempt.
 * It uses a different API endpoint and sequence-based data structure.
 * Kept for reference only - DO NOT UNCOMMENT OR USE.
 * =============================================================================
 */

/**
 * OLD Apollo Data Transformation Utilities - DO NOT USE
 */

// Rep name mapping: Apollo short names → Sales Dashboard full names
const REP_NAME_MAPPING = {
  'Matt': 'Matthew Vecchione',
  'Patrick': 'Patrick McTiernan',
  'Marcin': 'Marcin Maleski',
  'Damian': 'Damian Pomykala'
};

/**
 * Calculate week of month from date (W1-W5)
 */
function calculateWeek(dateString) {
  const date = new Date(dateString);
  const dayOfMonth = date.getDate();
  const weekNum = Math.ceil(dayOfMonth / 7);
  return 'W' + weekNum;
}

/**
 * Calculate month name from date
 */
function calculateMonth(dateString) {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const date = new Date(dateString);
  return monthNames[date.getMonth()];
}

/**
 * Transform Apollo data row to match Sales Dashboard format
 */
function transformApolloRow(row, sequenceName) {
  const fullName = REP_NAME_MAPPING[row['Rep Name']] || row['Rep Name'];
  const dateStr = row['Date (EDT)'];

  return {
    ...row,
    'Rep': fullName,                    // Full name for consistency
    'Rep_Original': row['Rep Name'],    // Keep original short name
    'Week': calculateWeek(dateStr),     // Add Week (W1-W5)
    'Month': calculateMonth(dateStr),   // Add Month (full name)
    'Sequence': sequenceName             // Add sequence name for filtering
  };
}

/**
 * Fetch data from Apollo API
 */
export async function fetchApolloData(action = 'list_tabs', params = {}) {
  try {
    const queryParams = new URLSearchParams({
      action: action,
      _t: Date.now(), // Cache-busting timestamp
      ...params
    });

    const url = `${API_CONFIG.APOLLO_API_URL}?${queryParams}`;
    console.log(`📡 Fetching from Apollo API: ${action}`, params);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Apollo data received:`, data);
    return data;
  } catch (error) {
    console.error('❌ Apollo API fetch error:', error);
    throw error;
  }
}

/**
 * React Hook - Fetch Apollo email performance data
 * Fetches all 17 sequences and aggregates
 * Auto-refreshes every 5 minutes
 */
export function useApolloData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFirstLoad = useRef(true);

  const loadData = async () => {
    try {
      // Only show loading spinner on first load
      if (isFirstLoad.current) {
        setLoading(true);
      }

      // Step 1: Get list of all sequence tabs
      const tabsResult = await fetchApolloData('list_tabs');
      const sequenceTabs = tabsResult.seq_tabs || [];

      console.log(`📊 Loading ${sequenceTabs.length} Apollo sequences...`);

      // Step 2: Fetch data from each sequence (all historical data)
      const allSequenceData = [];

      for (const tabName of sequenceTabs) {
        try {
          const encodedTab = encodeURIComponent(tabName);
          const tabResult = await fetchApolloData('get_tab_data', {
            tab: encodedTab,
            limit: 10000  // Get all historical data (currently ~2,337 rows across 17 sequences)
          });

          if (tabResult.success && tabResult.data_rows) {
            // Transform each row and add sequence name
            const transformedRows = tabResult.data_rows.map(row =>
              transformApolloRow(row, tabName)
            );

            allSequenceData.push({
              sequenceName: tabName,
              rows: transformedRows,
              totalRows: tabResult.total_rows
            });
          }
        } catch (err) {
          console.error(`⚠️  Failed to fetch sequence ${tabName}:`, err.message);
          // Continue with other sequences even if one fails
        }
      }

      // Step 3: Aggregate all data
      const allRows = allSequenceData.flatMap(seq => seq.rows);

      console.log(`✅ Loaded ${allRows.length} Apollo email records from ${allSequenceData.length} sequences`);

      setData({
        sequences: allSequenceData,
        allRows: allRows,
        sequenceCount: allSequenceData.length,
        totalRecords: allRows.length
      });
      setError(null);
      setLastUpdated(new Date());

      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    } catch (err) {
      setError(err.message);
      if (isFirstLoad.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    const wrappedLoadData = async () => {
      if (isMounted) {
        await loadData();
      }
    };

    wrappedLoadData();

    // Auto-refresh every 5 minutes (same as Salesforce data)
    const interval = setInterval(wrappedLoadData, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return { data, loading, error, lastUpdated, refresh: loadData };
}

/**
 * React Hook - Fetch Company Intel index (lightweight profile list)
 * Returns list of accounts that have research profiles with freshness metadata.
 */
export function useCompanyIntelIndex({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isLoadingRef = useRef(false);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const url = `${API_CONFIG.RESEARCH_API_URL}?action=getCompanyIntelIndex`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.success) {
        setData(result.profiles || []);
      } else {
        setData([]);
      }
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
      setData([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (autoLoad) loadData();
  }, []);

  return { data, loading, error, lastUpdated, refresh: loadData, load: loadData };
}

// Helper: parse GAS array-format response ({headers, rows}) into array of objects
function parseArrayFormat(result) {
  if (!result || !result.headers || !result.rows) return [];
  var headers = result.headers;
  return result.rows.map(function(row) {
    var obj = {};
    for (var i = 0; i < headers.length; i++) { obj[headers[i]] = row[i]; }
    return obj;
  });
}

// ws-sales-calls-sms-migration Phase 2.1 (D-SCSM-9) — short-range raw-data path.
// Phase 2 (D-SCSM-1..8) covered the daily-aggregate splice but ActivityTab.js has
// an aggregate-bypass for short ranges (today/yesterday/last7) that reads
// `data.tabs.Dials_TalkTime.data` directly — the legacy GAS source. This hook
// fetches canonical raw rows (Renewals scope, 30-day buffer window) so the
// short-range path can also read canonical via a new `canonicalRawCalls` prop
// on ActivityTab. SMS path untouched (D-SCSM-2: SMS stays on legacy).
export function useCanonicalCallsRaw({ autoLoad = false, days = 30, team = 'Renewals' } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isLoadingRef = useRef(false);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - days);
      const fmt = function(d) { return d.toISOString().split('T')[0]; };
      const rows = await fetchCanonicalCalls(fmt(start), fmt(end), team);
      setData(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (err) {
      console.error('❌ canonical calls raw load failed:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoLoad) loadData(); }, []);
  return { data, loading, error, load: loadData, refresh: loadData };
}

export function useActivityAggregates({ autoLoad = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isLoadingRef = useRef(false);

  const loadData = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    try {
      // ws-sales-calls-sms-migration D-SCSM-1/3/4/5 — fetch canonical calls
      // (last 13mo, team=Renewals) IN PARALLEL with the two legacy aggregates,
      // then splice canonical-computed calls fields into the daily aggregate rows.
      // Monthly aggregate stays fully legacy (deferred to follow-up). SMS fields
      // untouched per D-SCSM-2.
      const w = thirteenMonthWindow();
      const [dailyResult, monthlyResult, canonicalRaw] = await Promise.all([
        fetchData('agg_daily_activity', {}),
        fetchData('agg_monthly_activity', {}),
        fetchCanonicalCalls(w.start, w.end, 'Renewals')
      ]);

      let dailyRows = parseArrayFormat(dailyResult);
      if (canonicalRaw && canonicalRaw.length) {
        const canonicalIdx = buildDailyCallsIndex(canonicalRaw);
        dailyRows = spliceDailyCanonicalIntoLegacy(dailyRows, canonicalIdx);
        console.log('📊 [Tier 2] canonical-splice applied: ' + canonicalRaw.length + ' raw calls → ' + Object.keys(canonicalIdx).length + ' (Rep, Date) buckets overridden');
      }

      setData({
        daily: dailyRows,
        monthly: monthlyResult.rows || []
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoLoad) loadData(); }, []);

  return { data, loading, error, load: loadData };
}
